import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import LibAccordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import { theme } from '_constants/theme';

const { colors, fontSizes, spaces, devices } = theme;

const StyledContainer = styled.div`
  background: ${colors.gray[100]};
  border-radius: 16px;
  padding: ${spaces.regular}px;
  gap: ${spaces.regular}px;
  display: flex;
  flex-direction: column;
  ${({ open }) =>
    open &&
    css`
      background: ${colors.indigo[50]};
    `}
  @media ${devices.tablet} {
    padding: ${spaces.smallX}px;
    gap: ${spaces.smallX}px;
    border-radius: 10px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  cursor: pointer;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  @media ${devices.tablet} {
    gap: 10px;
  }
`;
const StyledCollapseContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  @media ${devices.tablet} {
    gap: 10px;
  }
`;
const StyledIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  width: 50px;
  height: 50px;
  min-width: 50px;
  background: ${colors.gray[300]};
  border-radius: 8px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large2X}px;
  line-height: 150%;
  text-align: center;
  color: ${colors.gray[600]};
  ${({ open }) =>
    open &&
    css`
      background: ${colors.white};
    `}
  ${({ answer }) =>
    answer &&
    css`
      color: ${colors.indigo[600]};
      background: ${colors.indigo[200]};
    `}
  @media ${devices.tablet} {
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 6px;
    padding: ${spaces.smallX}px;
    font-size: ${fontSizes.medium}px;
  }
`;

const StyledTitle = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  display: flex;
  align-items: center;
  color: ${colors.gray[600]};
  ${({ open }) =>
    open &&
    css`
      color: ${colors.indigo[900]};
    `}
  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
    line-height: 24px;
  }
`;

const StyledBody = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  display: flex;
  align-items: center;
  color: ${colors.gray[600]};
  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
  }
`;

const AccordionItem = ({ eventKey, title, children, open, onToggle }) => {
  const handleToggle = useAccordionButton(
    eventKey,
    () => onToggle && onToggle(eventKey)
  );

  return (
    <StyledContainer open={open}>
      <StyledHeader onClick={handleToggle}>
        <StyledIconContainer open={open}>Q</StyledIconContainer>
        <StyledTitle open={open}>{title}</StyledTitle>
      </StyledHeader>
      <LibAccordion.Collapse eventKey={eventKey}>
        <StyledCollapseContainer>
          <StyledIconContainer answer={true}>A</StyledIconContainer>
          <StyledBody>{children}</StyledBody>
        </StyledCollapseContainer>
      </LibAccordion.Collapse>
    </StyledContainer>
  );
};

AccordionItem.propTypes = {
  eventKey: PropTypes.string,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default AccordionItem;
